import { useState, useEffect } from "react";
import { ref, onValue, push } from "firebase/database";
import { getAuth } from "firebase/auth";
import { db as database } from "../Firebase/firestore";
import "./broadcast.css"

const BroadcastingPage = () => {
  // State management
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [activeTab, setActiveTab] = useState("compose");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  // Form states
  const [heading, setHeading] = useState("");
  const [message, setMessage] = useState("");
  const [validity, setValidity] = useState("2 days");
  const [recipientType, setRecipientType] = useState("all");

  // Get current admin/sender ID from Firebase Auth
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid || "";

  // Debug authentication on mount
  useEffect(() => {
    console.log("Current user:", auth.currentUser);
    if (!auth.currentUser) {
      showNotification("Please sign in to send broadcasts", "error");
    }
  }, []);

  // Show notification
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch users from Firebase
  useEffect(() => {
    const profileRef = ref(database, "profileDashboard");
    const unsubscribe = onValue(
      profileRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const userList = Object.keys(data).map((userId) => ({
            userId,
            company_name: data[userId].company_name || "Unknown Company",
          }));
          const sortedList = userList.sort((a, b) => a.company_name.localeCompare(b.company_name));
          setUsers(userList);
          console.log("Fetched users:", userList);
        } else {
          console.warn("No data in profileDashboard");
          setUsers([]);
          showNotification("No users found in profileDashboard", "error");
        }
      },
      (error) => {
        console.error("Error fetching users:", error.code, error.message);
        showNotification(`Error fetching users: ${error.message}`, "error");
      }
    );

    return () => unsubscribe();
  }, []);

  // Fetch broadcasts for the current user (sender) from Firebase
  useEffect(() => {
    if (!currentUserId) {
      console.warn("No authenticated user, skipping broadcast fetch");
      return;
    }
    const broadcastRef = ref(database, `broadcasts/${currentUserId}`);
    const unsubscribe = onValue(
      broadcastRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const broadcastList = Object.keys(data).map((id) => ({
            id,
            ...data[id],
          }));
          setBroadcasts(broadcastList);
          console.log("Fetched broadcasts:", broadcastList);
        } else {
          setBroadcasts([]);
        }
      },
      (error) => {
        console.error("Error fetching broadcasts:", error.code, error.message);
        showNotification(`Error fetching broadcasts: ${error.message}`, "error");
      }
    );

    return () => unsubscribe();
  }, [currentUserId]);

  // Handle user selection
  const handleUserSelect = (userId, checked) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  };

  // Select all users
  const handleSelectAll = () => {
    setSelectedUsers(users.map((user) => user.userId));
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  // Validate form
  const validateForm = () => {
    if (!currentUserId) {
      showNotification("User not authenticated", "error");
      return false;
    }
    if (!heading.trim()) {
      showNotification("Please enter a heading", "error");
      return false;
    }
    if (!message.trim()) {
      showNotification("Please enter a message", "error");
      return false;
    }
    if (recipientType === "selected" && selectedUsers.length === 0) {
      showNotification("Please select at least one recipient", "error");
      return false;
    }
    return true;
  };

  // Send broadcast
  const handleSendBroadcast = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const recipients = recipientType === "all" ? users.map((user) => user.userId) : selectedUsers;
      if (recipients.length === 0) {
        showNotification("No recipients available", "error");
        setLoading(false);
        return;
      }

      console.log("Sending broadcast to recipients:", recipients);

      const broadcastData = {
        heading,
        message,
        validity,
        senderId: currentUserId,
        timestamp: new Date().toISOString(),
        status: "sent",
        recipients: recipients, // Store recipient list for sender's copy
      };

      // Push broadcast to each recipient's node and a copy to sender's node
      const promises = [
        push(ref(database, `broadcasts/${currentUserId}`), broadcastData),
        ...recipients.map((recipientId) =>
          push(ref(database, `broadcasts/${recipientId}`), broadcastData)
        ),
      ];

      await Promise.all(promises);

      // Clear form
      setHeading("");
      setMessage("");
      setValidity("2 days");
      setSelectedUsers([]);
      setRecipientType("all");

      showNotification(`Broadcast sent to ${recipients.length} users`);
      setActiveTab("sent");
    } catch (error) {
      console.error("Broadcast error details:", error.code, error.message);
      showNotification(`Failed to send broadcast: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Save as draft
  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const recipients = recipientType === "all" ? users.map((user) => user.userId) : selectedUsers;
      if (recipients.length === 0) {
        showNotification("No recipients available", "error");
        setLoading(false);
        return;
      }

      console.log("Saving draft for recipients:", recipients);

      const draftData = {
        heading,
        message,
        validity,
        senderId: currentUserId,
        timestamp: new Date().toISOString(),
        status: "draft",
        recipients: recipients, // Store recipient list for sender's copy
      };

      // Push draft to each recipient's node and a copy to sender's node
      const promises = [
        push(ref(database, `broadcasts/${currentUserId}`), draftData),
        ...recipients.map((recipientId) =>
          push(ref(database, `broadcasts/${recipientId}`), draftData)
        ),
      ];

      await Promise.all(promises);

      showNotification("Draft saved successfully");
      setActiveTab("drafts");
    } catch (error) {
      console.error("Draft error details:", error.code, error.message);
      showNotification(`Failed to save draft: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Filter broadcasts
  const sentBroadcasts = broadcasts.filter((b) => b.status === "sent");
  const draftBroadcasts = broadcasts.filter((b) => b.status === "draft");

  return (
    <div className="container" style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
      {/* Notification */}
      {notification.show && (
        <div
          className={`notification ${notification.type === "error" ? "notification-error" : "notification-success"}`}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            padding: "15px 20px",
            borderRadius: "4px",
            color: "#fff",
            backgroundColor: notification.type === "error" ? "#e74c3c" : "#2ecc71",
            zIndex: 1000,
          }}
        >
          {notification.message}
        </div>
      )}

      <div className="main-content">
        <div className="header" style={{ textAlign: "center", marginBottom: "20px" }}>
          <h1 className="title" style={{ fontSize: "2rem", fontWeight: "bold" }}>
            📡 Broadcasting Dashboard
          </h1>
          <p className="subtitle" style={{ color: "#666", fontSize: "1rem" }}>
            Compose and manage broadcast messages
          </p>
        </div>

        {/* Tabs */}
        <div
          className="tabs"
          style={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "20px" }}
        >
          <button
            className={`tab ${activeTab === "compose" ? "active-tab" : ""}`}
            onClick={() => setActiveTab("compose")}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              background: activeTab === "compose" ? "#3498db" : "#f0f0f0",
              color: activeTab === "compose" ? "#fff" : "#333",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            ✏️ Compose
          </button>
          <button
            className={`tab ${activeTab === "drafts" ? "active-tab" : ""}`}
            onClick={() => setActiveTab("drafts")}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              background: activeTab === "drafts" ? "#3498db" : "#f0f0f0",
              color: activeTab === "drafts" ? "#fff" : "#333",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            📄 Drafts ({draftBroadcasts.length})
          </button>
          <button
            className={`tab ${activeTab === "sent" ? "active-tab" : ""}`}
            onClick={() => setActiveTab("sent")}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              background: activeTab === "sent" ? "#3498db" : "#f0f0f0",
              color: activeTab === "sent" ? "#fff" : "#333",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            ✅ Sent ({sentBroadcasts.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Compose Tab */}
          {activeTab === "compose" && (
            <div
              className="compose-section"
              style={{
                background: "#fff",
                borderRadius: "8px",
                padding: "20px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                className="card-title"
                style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "20px" }}
              >
                Compose New Broadcast
              </h3>

              <div className="form-group" style={{ marginBottom: "15px" }}>
                <label className="label" style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                  Heading *
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Enter broadcast heading"
                  value={heading}
                  onChange={(e) => setHeading(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "1rem",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "15px" }}>
                <label className="label" style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                  Message *
                </label>
                <textarea
                  className="textarea"
                  placeholder="Enter your broadcast message"
                  rows="6"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "1rem",
                    resize: "vertical",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "15px" }}>
                <label className="label" style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                  Validity
                </label>
                <select
                  className="select"
                  value={validity}
                  onChange={(e) => setValidity(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "1rem",
                  }}
                >
                  <option value="1 day">1 day</option>
                  <option value="2 days">2 days</option>
                  <option value="1 week">1 week</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="1 month">1 month</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "15px" }}>
                <label className="label" style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                  Recipients
                </label>
                <div className="radio-group" style={{ display: "flex", gap: "20px" }}>
                  <label className="radio-label" style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <input
                      type="radio"
                      name="recipientType"
                      value="all"
                      checked={recipientType === "all"}
                      onChange={(e) => setRecipientType(e.target.value)}
                      className="radio"
                    />
                    All Users ({users.length})
                  </label>
                  <label className="radio-label" style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <input
                      type="radio"
                      name="recipientType"
                      value="selected"
                      checked={recipientType === "selected"}
                      onChange={(e) => setRecipientType(e.target.value)}
                      className="radio"
                    />
                    Selected Users ({selectedUsers.length})
                  </label>
                </div>
              </div>

              {recipientType === "selected" && (
                <div
                  className="user-selection"
                  style={{
                    marginBottom: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "15px",
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <h4 style={{ fontSize: "1.1rem", fontWeight: "500" }}>
                      Select Users ({selectedUsers.length} selected)
                    </h4>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        onClick={handleSelectAll}
                        style={{
                          padding: "5px 10px",
                          background: "#3498db",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={handleClearSelection}
                        style={{
                          padding: "5px 10px",
                          background: "#e74c3c",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  {users.length === 0 ? (
                    <p style={{ color: "#666" }}>No users available</p>
                  ) : (
                    users.map((user) => (
                      <div
                        key={user.userId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "5px 0",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.userId)}
                          onChange={(e) => handleUserSelect(user.userId, e.target.checked)}
                          style={{ marginRight: "10px" }}
                        />
                        <div>
                          <div style={{ fontWeight: "500" }}>{user.company_name}</div>
                          <div style={{ fontSize: "0.8rem", color: "#666" }}>
                            ID: {user.userId.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              <div
                className="button-group"
                style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
              >
                <button
                  className="primary-button"
                  onClick={handleSendBroadcast}
                  disabled={loading || !currentUserId}
                  style={{
                    padding: "10px 20px",
                    background: loading || !currentUserId ? "#95a5a6" : "#2ecc71",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: loading || !currentUserId ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  {loading ? "📤 Sending..." : "📤 Send Broadcast"}
                </button>
                <button
                  className="secondary-button"
                  onClick={handleSaveDraft}
                  disabled={loading || !currentUserId}
                  style={{
                    padding: "10px 20px",
                    background: loading || !currentUserId ? "#95a5a6" : "#3498db",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: loading || !currentUserId ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  💾 Save as Draft
                </button>
              </div>
            </div>
          )}

          {/* Drafts Tab */}
          {activeTab === "drafts" && (
            <div className="broadcast-list">
              {draftBroadcasts.length === 0 ? (
                <div
                  className="empty-state"
                  style={{ textAlign: "center", padding: "20px", color: "#666" }}
                >
                  <div className="empty-icon" style={{ fontSize: "2rem", marginBottom: "10px" }}>
                    📄
                  </div>
                  <h3 className="empty-title" style={{ fontSize: "1.2rem", fontWeight: "600" }}>
                    No drafts yet
                  </h3>
                  <p className="empty-text">Your saved draft messages will appear here</p>
                </div>
              ) : (
                draftBroadcasts.map((broadcast) => (
                  <div
                    key={broadcast.id}
                    className="broadcast-card"
                    style={{
                      background: "#fff",
                      borderRadius: "8px",
                      padding: "15px",
                      marginBottom: "10px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      className="broadcast-header"
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <h4 className="broadcast-title" style={{ fontSize: "1.1rem", fontWeight: "500" }}>
                        {broadcast.heading}
                      </h4>
                      <span
                        className="draft-badge"
                        style={{
                          padding: "5px 10px",
                          background: "#f1c40f",
                          color: "#fff",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                        }}
                      >
                        Draft
                      </span>
                    </div>
                    <p
                      className="broadcast-date"
                      style={{ fontSize: "0.9rem", color: "#666", margin: "5px 0" }}
                    >
                      💾 Saved on {new Date(broadcast.timestamp).toLocaleDateString()}
                    </p>
                    <p
                      className="broadcast-message"
                      style={{ fontSize: "1rem", color: "#333", margin: "5px 0" }}
                    >
                      {broadcast.message.length > 150
                        ? `${broadcast.message.substring(0, 150)}...`
                        : broadcast.message}
                    </p>
                    <div
                      className="broadcast-footer"
                      style={{ fontSize: "0.9rem", color: "#666", display: "flex", gap: "20px" }}
                    >
                      <span>Recipients: {broadcast.recipients?.length || 0} users</span>
                      <span>Validity: {broadcast.validity}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Sent Tab */}
          {activeTab === "sent" && (
            <div className="broadcast-list">
              {sentBroadcasts.length === 0 ? (
                <div
                  className="empty-state"
                  style={{ textAlign: "center", padding: "20px", color: "#666" }}
                >
                  <div className="empty-icon" style={{ fontSize: "2rem", marginBottom: "10px" }}>
                    📤
                  </div>
                  <h3 className="empty-title" style={{ fontSize: "1.2rem", fontWeight: "600" }}>
                    No broadcasts sent yet
                  </h3>
                  <p className="empty-text">Your sent broadcast messages will appear here</p>
                </div>
              ) : (
                sentBroadcasts.map((broadcast) => (
                  <div
                    key={broadcast.id}
                    className="broadcast-card"
                    style={{
                      background: "#fff",
                      borderRadius: "8px",
                      padding: "15px",
                      marginBottom: "10px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      className="broadcast-header"
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    >
                      <h4 className="broadcast-title" style={{ fontSize: "1.1rem", fontWeight: "500" }}>
                        {broadcast.heading}
                      </h4>
                      <span
                        className="sent-badge"
                        style={{
                          padding: "5px 10px",
                          background: "#2ecc71",
                          color: "#fff",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                        }}
                      >
                        Sent
                      </span>
                    </div>
                    <p
                      className="broadcast-date"
                      style={{ fontSize: "0.9rem", color: "#666", margin: "5px 0" }}
                    >
                      ✅ Sent on {new Date(broadcast.timestamp).toLocaleDateString()}
                    </p>
                    <p
                      className="broadcast-message"
                      style={{ fontSize: "1rem", color: "#333", margin: "5px 0" }}
                    >
                      {broadcast.message.length > 150
                        ? `${broadcast.message.substring(0, 150)}...`
                        : broadcast.message}
                    </p>
                    <div
                      className="broadcast-footer"
                      style={{ fontSize: "0.9rem", color: "#666", display: "flex", gap: "20px" }}
                    >
                      <span>Recipients: {broadcast.recipients?.length || 0} users</span>
                      <span>Validity: {broadcast.validity}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BroadcastingPage;